import { WebXRControllerComponent } from '@babylonjs/core/XR/motionController/webXRControllerComponent';
import type { WebXRDefaultExperience } from '@babylonjs/core/XR/webXRDefaultExperience';
import { ForfeitLatch, Latch, type FighterInputSource, type FighterIntent } from './fighterInput';
import { aims, applyDeadZone } from './stick';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { Camera } from '@babylonjs/core/Cameras/camera';
import { controllerHandedness } from './xrTableInput';

/** Arena axes stay fixed; controller directions follow the view of the table, not world +Z. */
export function arenaStickDirection(x: number, z: number, camera: Camera) {
  camera.computeWorldMatrix();
  const right = camera.getDirection(Vector3.Right());
  const up = camera.getDirection(Vector3.Up());
  right.y = 0;
  up.y = 0;
  if (up.lengthSquared() < 1e-6) {
    up.copyFrom(camera.getForwardRay().direction);
    up.y = 0;
  }
  right.normalize();
  up.normalize();
  const direction = right.scale(x).addInPlace(up.scale(z));
  direction.normalize().scaleInPlace(Math.min(1, Math.hypot(x, z)));
  return { x: direction.x, z: direction.z };
}

/**
 * Meta Quest controls under the fixed camera: either stick moves the fighter about the diorama,
 * A or X dashes and either trigger attacks. Holding fire borrows the right stick to aim and then
 * bend a steerable spellcaster projectile; releasing it returns the stick to movement. A hand
 * whose grip is held points instead: its laser works the radio and VR plate, its trigger clicks.
 * B or Y held down gives the duel up, as a pad's Start does: the headset had no way out of a
 * fight short of leaving VR (client-input:quality#1).
 *
 * Nothing here moves the player. The arena is a model on the table they are already sitting at
 * (D-16), so there is no locomotion, no snap turn and nothing to be made uncomfortable by.
 */
export function createXrFighterInput(xr: WebXRDefaultExperience): FighterInputSource {
  const dash = new Latch();
  const attack = new Latch();
  let moveX = 0;
  let moveZ = 0;
  let aimX = 0;
  let aimZ = 0;
  let attackHeldNow = false;
  const forfeit = new ForfeitLatch('B or Y');

  return {
    update(dt) {
      let dashHeld = false;
      let attackHeld = false;
      let backHeld = false;
      moveX = 0;
      moveZ = 0;
      aimX = 0;
      aimZ = 0;
      for (const controller of xr.input.controllers) {
        const motion = controller.motionController;
        if (!motion) continue;
        const stick = motion.getComponentOfType(WebXRControllerComponent.THUMBSTICK_TYPE);
        const trigger = motion.getComponentOfType(WebXRControllerComponent.TRIGGER_TYPE);
        // A held grip points the laser at the controls plate instead (`createImmersiveControls`):
        // that trigger clicks there and does not attack.
        const grip = motion.getComponentOfType(WebXRControllerComponent.SQUEEZE_TYPE);
        if (trigger && !grip?.pressed && (trigger.pressed || trigger.value > 0.6))
          attackHeld = true;
        const face = motion.getComponent('a-button') ?? motion.getComponent('x-button');
        if (face?.pressed) dashHeld = true;
        const back = motion.getComponent('b-button') ?? motion.getComponent('y-button');
        if (back?.pressed) backHeld = true;
        if (!stick) continue;
        // Keep screen axes until next(), where the current XR view maps them onto the arena. Raw:
        // `applyDeadZone` there is round, and a dead zone per axis first squared off gentle
        // diagonals (client-input:bugs#7).
        if (controllerHandedness(controller) === 'right') {
          aimX = stick.axes.x;
          aimZ = -stick.axes.y;
        } else {
          moveX = stick.axes.x;
          moveZ = -stick.axes.y;
        }
      }
      dash.set(dashHeld);
      attack.set(attackHeld);
      attackHeldNow = attackHeld;
      forfeit.set(backHeld, dt);
    },
    next(mode): FighterIntent {
      const magicAim = !!mode?.steerableProjectile && attackHeldNow && !mode.projectileInFlight;
      const magicSteer = !!mode?.steerableProjectile && mode.projectileInFlight && attackHeldNow;
      const left = applyDeadZone(moveX, moveZ);
      const right = applyDeadZone(aimX, aimZ);
      // Both sticks at once: the left moves and the right aims; one alone moves and faces, as on
      // a pad (client-input:bugs#5).
      const twoSticks = (left.x !== 0 || left.z !== 0) && aims(right.x, right.z);
      const walking =
        magicAim ||
        magicSteer ||
        twoSticks ||
        Math.hypot(left.x, left.z) >= Math.hypot(right.x, right.z)
          ? left
          : right;
      const movement = arenaStickDirection(walking.x, walking.z, xr.baseExperience.camera);
      const aiming = arenaStickDirection(right.x, right.z, xr.baseExperience.camera);
      return {
        moveX: movement.x,
        moveZ: movement.z,
        aimAt: null,
        aimDir:
          (magicAim || twoSticks) && aims(right.x, right.z)
            ? aiming
            : !magicSteer && aims(walking.x, walking.z)
              ? movement
              : null,
        dash: dash.take(),
        attack: attack.take(),
        steer: magicSteer ? right.x : 0,
        forfeit: forfeit.take(),
      };
    },
    forfeitHold: () => forfeit.progress(),
    pulse(strength, seconds) {
      // Both hands: a blow is felt by the body, not by the hand that swung.
      for (const controller of xr.input.controllers) {
        try {
          void controller.motionController?.pulse(strength, seconds * 1000).catch(() => false);
        } catch {
          // A controller without haptics: nothing is felt.
        }
      }
    },
    dispose() {
      forfeit.reset();
    },
  };
}
